import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Wrapper from '../../components/Wrapper';
import CsrKpiCard from '../../components/CsrDashboard/CsrKpiCard';
import CsrPageHeader from '../../components/CsrDashboard/CsrPageHeader';
import { CsrError, CsrLoading } from '../../components/CsrDashboard/CsrStatus';
import ImpactSignalCards from '../../components/CsrDashboard/ImpactSignalCards';
import IndiaStateMap from '../../components/CsrDashboard/IndiaStateMap';
import DistrictImpactPanel from '../../components/CsrDashboard/DistrictImpactPanel';
import NoDataStateNotice from '../../components/CsrDashboard/NoDataStateNotice';
import { CSR_IMPACT_PLACEHOLDERS } from '../../components/CsrDashboard/csrPlaceholders';
import { stateKey } from '../../components/CsrDashboard/indiaStatePaths';
import { fmt, plural, titleCase } from '../../components/CsrDashboard/csrFormat';
import { useCsrCoverage, useCsrMetrics } from '../../hooks/useCsrDashboard';
import '../../styles/csrDashboard.css';

const CsrOverview = () => {
  // Support opens /csr/overview/:adminId; partners and team members use their own login scope.
  const { adminId } = useParams();
  const csr = useCsrCoverage(adminId);
  const {
    coverage, coverageStatus, coverageError, loadCoverage, activeStates, coverageByKey,
    scope, setScope, scopeRow, isStateScope, isNoDataScope, scopeName,
  } = csr;
  const { metrics, metricsStatus } = useCsrMetrics(adminId, coverageStatus === 'success' && !isNoDataScope, scopeRow?.state);

  const [district, setDistrict] = useState(null);
  const [layer, setLayer] = useState('tests');
  const [bars, setBars] = useState('both');

  const selectState = (key) => { setScope(key); setDistrict(null); };
  const resetScope = () => { setScope('all'); setDistrict(null); };

  // ── KPI values ──
  const districtsCovered = isStateScope
    ? scopeRow.districts_covered
    : activeStates.reduce((n, s) => n + s.districts_covered, 0);
  const crops = isStateScope
    ? scopeRow.crops
    : [...new Set(activeStates.flatMap((s) => s.crops))].sort();
  const cropSub = crops.length
    ? `${crops.slice(0, 3).map(titleCase).join(', ')}${crops.length > 3 ? ` +${crops.length - 3}` : ''}`
    : 'none recorded yet';
  const metric = (key) => (metricsStatus === 'success' ? fmt(metrics?.[key]) : metricsStatus === 'loading' ? '…' : '—');

  const kpis = [
    { label: 'Soil tests completed', value: metric('soilReports'), sub: '16 parameters each' },
    { label: 'Farmers reached', value: metric('farmers'), sub: 'registered with this partner' },
    { label: 'Acres covered', value: metric('totalArea'), sub: 'under registered farms' },
    { label: 'Districts covered', value: fmt(districtsCovered), sub: isStateScope ? `in ${scopeName}` : `across ${plural(activeStates.length, 'active state', 'active states')}` },
    { label: 'Crops covered', value: fmt(crops.length), sub: cropSub },
    { label: 'Livelihoods created', value: metric('fieldOfficers'), sub: isStateScope ? `field officers based in ${scopeName}` : 'field officers' },
  ];

  const districtsInScope = isStateScope ? (coverage?.districts || []).filter((d) => stateKey(d.state) === scope) : [];
  const unspecified = coverage?.unspecified_state || { farmers: 0, soil_tests: 0 };
  const hasNoData = coverageStatus === 'success' && activeStates.length === 0 && !unspecified.farmers && !unspecified.soil_tests;

  return (
    <Wrapper>
      <div className="csr-dash">
        <div className="csr-wrap">
          <CsrPageHeader
            title="CSR Impact Overview"
            subtitle="Reach, soil testing and state coverage"
            csr={csr}
            adminId={adminId}
            onScopeChange={selectState}
          />

          {coverageStatus === 'loading' ? <CsrLoading /> : null}
          {coverageStatus === 'error' ? <CsrError message={coverageError} onRetry={loadCoverage} /> : null}

          {coverageStatus === 'success' ? (
            <div className="csr-reveal d-flex flex-column" style={{ gap: 20 }}>
              {hasNoData ? (
                <p className="csr-inline-warn">No farmers or soil tests are recorded for this partner yet. The dashboard fills in automatically as data arrives.</p>
              ) : null}

              {isNoDataScope ? (
                <NoDataStateNotice
                  stateName={scopeName}
                  activeStateNames={activeStates.map((s) => s.state)}
                  onReset={resetScope}
                />
              ) : (
                <>
                  <section aria-label="State coverage summary" className="d-flex flex-column" style={{ gap: 8 }}>
                    <div className="csr-kpis">
                      {kpis.map((k) => <CsrKpiCard key={k.label} {...k} />)}
                    </div>
                    {metricsStatus === 'error' ? (
                      <p className="csr-inline-warn">Some figures could not be loaded right now. Refresh the page to try again.</p>
                    ) : null}
                  </section>
                  <ImpactSignalCards impact={CSR_IMPACT_PLACEHOLDERS} />
                </>
              )}

              <div className="csr-split">
                <IndiaStateMap
                  coverage={coverageByKey}
                  scopeKey={scope}
                  layer={layer}
                  onLayerChange={setLayer}
                  onSelectState={selectState}
                  onReset={resetScope}
                  unspecifiedFarmers={unspecified.farmers}
                />
                {!isNoDataScope ? (
                  <DistrictImpactPanel
                    scopeName={scopeName}
                    isStateScope={isStateScope}
                    activeStates={activeStates}
                    districts={districtsInScope}
                    selectedDistrict={district}
                    onSelectState={selectState}
                    onSelectDistrict={setDistrict}
                    onClearDistrict={() => setDistrict(null)}
                    bars={bars}
                    onBarsChange={setBars}
                    score={CSR_IMPACT_PLACEHOLDERS.soilScore}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default CsrOverview;